import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setTokenRefreshFunction } from '@/lib/api';

/**
 * Hook to automatically refresh Auth0 token proactively
 * Refreshes every 5 minutes to prevent expiration issues
 */
export function useTokenRefresh() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setTokenRefreshFunction(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Create refresh function that can be called by API client (returns token string)
    const refreshToken = async (): Promise<string> => {
      console.log('[TokenRefresh] Refreshing token...');
      try {
        const newToken = await getAccessTokenSilently();
        localStorage.setItem('auth_token', newToken);
        console.log('[TokenRefresh] Token refreshed successfully');
        return newToken;
      } catch (error: any) {
        console.error('[TokenRefresh] Token refresh failed:', error);
        // If refresh fails with "Login required" (user session expired after inactivity),
        // redirect to the appropriate login page.
        if (error?.message?.includes('Login required') || error?.error === 'login_required') {
          console.warn('[TokenRefresh] Session expired - redirecting to login');
          const userType = localStorage.getItem('userType') as 'doctor' | 'parent' | null;
          const loginPath = userType === 'doctor' ? '/doctor/login' : '/parent/login';
          // Clear auth state before redirect
          localStorage.removeItem('auth_token');
          localStorage.removeItem('userType');
          localStorage.removeItem('userType_doctor');
          localStorage.removeItem('userType_parent');
          globalThis.location.href = loginPath;
        }
        throw error;
      }
    };

    // Register refresh function with API client
    setTokenRefreshFunction(refreshToken);

    // Initial token fetch on mount (best effort)
    refreshToken().catch((error) => {
      console.error('[TokenRefresh] Initial token fetch failed:', error);
    });

    // Proactively refresh token every 5 minutes to prevent expiry
    intervalRef.current = setInterval(() => {
      console.log('[TokenRefresh] Proactive refresh triggered');
      refreshToken().catch((error) => {
        console.error('[TokenRefresh] Proactive refresh failed:', error);
      });
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      setTokenRefreshFunction(null);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAuthenticated, getAccessTokenSilently]);
}
